import { useGetPreviousStep } from "@/frontend/hooks/useGetPreviousStep";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { CircleX } from "@/frontend/SVGs/CircleX";
import { Completed } from "@/frontend/SVGs/Completed";
import { Incomplete } from "@/frontend/SVGs/Incomplete";
import { I18n } from "@/i18n/I18n";
import { Status } from "@/types/containerTypes";
import { CONTEXT_LOG_SUBJECT_TYPES } from "@/types/contextLogTypes";
import { ParameterSubjectType } from "@/types/parameterTypes";
import { Step } from "@/backend/models/containers/step";
import { Dropdown } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import { CustomToggle } from "../dropdown/CustomToggle";
import ContextLogViewer from "../contextLogs/ContextLogViewer";


export const statusIcon = {
  [Status.PENDING]: <div className='pending-icon'><Incomplete /></div>,
  [Status.PASSED]:  <div className='completion-icon'><Completed /></div>,
  [Status.FAILED]:  <div className='failed-icon'><CircleX /></div>,
  [Status.ABORTED]: <div className='aborted-icon'><CircleX /></div>,
  [Status.DEVIATED]: <div className='deviated-icon'><CircleX /></div>,
};

export interface StepItemRunHeaderProps {
  step: Step;
  setShowStep?: (showStep: boolean) => void;
  showStep?: boolean;
}

export function StepItemRunHeader(props: StepItemRunHeaderProps) {
  const { step, setShowStep, showStep = true } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const removeContextLogsForStep = useParameterStore((state) => state.deleteContextLogsForSubject);
  const overrideStepStart = useTreeStore(state => state.overrideStepStart);
  const updateStatus = useTreeStore(state => state.updateStatus);
  const treeObject = useTreeStore(state => state.containerLookup[step.id]);
  const previousStep = useGetPreviousStep(treeObject?.stepPosition);
  const stepStarted = !previousStep || previousStep.container.status !== Status.PENDING || treeObject?.stepStartOverridden;
  const contextLogs = useParameterStore(useShallow(state => state.contextLogs.filter((log) => log.subjectId === step.id && log.subjectType === CONTEXT_LOG_SUBJECT_TYPES.STEP)));
  const parameters = useParameterStore(useShallow(state => state.parametersForSubject(step.id, ParameterSubjectType.STEP)));

  const resetStatus = () => {
    void removeContextLogsForStep(step.id, CONTEXT_LOG_SUBJECT_TYPES.STEP);
    void updateStatus(step, Status.PENDING);
    setShowStep?.(true);
  };

  const overriden = step.status === Status.PASSED && contextLogs.length > 0;
  const statusIconComponent = overriden ? <div className='overridden-icon link-warning'><Completed /></div> : statusIcon[step.status];

  return (
    <div className="card-header">
      <div className="d-flex pt-1 justify-content-between">
        <div className="d-flex align-items-center gap-1">
          {setShowStep && <CollapseButton showElement={showStep} setShowElement={setShowStep} title={I18n.get(step.containerType)}/>}
          <h6 className='mb-0 pe-1'>{step.title}</h6>
          {statusIconComponent}
          <ContextLogViewer subjectId={step.id} subjectType={CONTEXT_LOG_SUBJECT_TYPES.STEP} />
        </div>
        <div className='d-flex'>
          <Dropdown drop='down'>
            <Dropdown.Toggle size='sm' as={CustomToggle(I18n.get('step'))} variant='dark' id="dropdown-basic" />
            <Dropdown.Menu>
              {!location.pathname.includes('wizard') ? <Dropdown.Item onClick={() => void navigate(`/procedures/wizard/${step.rootId}/steps/${step.id}`)}>{I18n.get('viewInWizard')}</Dropdown.Item> : null}
              {location.pathname.includes('wizard') ? <Dropdown.Item onClick={() => void navigate(`/procedures/run/${step.rootId}#${step.id}`)}>{I18n.get('viewInRunView')}</Dropdown.Item> : null}
              <Dropdown.Item disabled={step.status === Status.PENDING} onClick={resetStatus}>{I18n.get('resetStatus')}</Dropdown.Item>
              <Dropdown.Item disabled={stepStarted} onClick={() => overrideStepStart(step.id)}>{I18n.get('startThisStep')}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {!showStep && parameters.map(parameter => <div id={parameter.id} key={parameter.id} />)}
    </div>
  );
}
